import React, { useState, useEffect, useCallback } from 'react';
import {
  ProfessionalCard,
  ProfessionalButton,
  StatusIndicator,
  ProgressBar,
  MetricCard,
  Tabs
} from './ProfessionalUI';
import './CodeAnalysisDashboard.css';

interface CodeMetrics {
  linesOfCode: number;
  functions: number;
  classes: number;
  complexity: number;
  maintainability: number;
  testCoverage: number;
  securityScore: number;
  performance: number;
}

interface AnalysisResult {
  summary: string;
  components: string[];
  patterns: string[];
  issues: string[];
  recommendations: string[];
  metrics: CodeMetrics;
}

interface CodeAnalysisDashboardProps {
  code: string;
  language: string;
  fileName: string;
  onAnalysisComplete?: (result: AnalysisResult) => void;
}

export const CodeAnalysisDashboard: React.FC<CodeAnalysisDashboardProps> = ({
  code,
  language,
  fileName,
  onAnalysisComplete
}) => {
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setActiveTab] = useState('overview');

  // 模拟分析过程
  const analyzeCode = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // 模拟 API 调用
      await new Promise(resolve => setTimeout(resolve, 2000));

      // 计算基础指标
      const lines = code.split('\n').length;
      const functions = (code.match(/function\s+\w+|def\s+\w+|class\s+\w+/g) || []).length;
      const classes = (code.match(/class\s+\w+/g) || []).length;

      // 生成模拟分析结果
      const result: AnalysisResult = {
        summary: `该${language}文件展现了良好的代码结构和现代化编程实践。代码组织清晰，包含了${functions}个主要函数和${classes}个类定义。整体架构遵循了SOLID原则，具有良好的可维护性和扩展性。`,
        components: [
          '主控制器模块',
          '数据处理组件',
          '配置管理器',
          '日志记录系统',
          '错误处理机制'
        ],
        patterns: [
          '单例模式 (Singleton)',
          '工厂模式 (Factory)',
          '观察者模式 (Observer)',
          '策略模式 (Strategy)',
          '依赖注入 (Dependency Injection)'
        ],
        issues: [
          '建议添加更多的单元测试',
          '部分函数可以进一步拆分以提高可读性',
          '考虑添加类型注解以提高代码安全性',
          '建议使用更现代的ES6+语法特性'
        ],
        recommendations: [
          '增加错误处理边界情况',
          '优化算法复杂度',
          '添加代码文档和注释',
          '实施代码审查流程',
          '集成自动化测试工具'
        ],
        metrics: {
          linesOfCode: lines,
          functions: functions,
          classes: classes,
          complexity: Math.min(Math.max(Math.floor(Math.random() * 40) + 20, 20), 100),
          maintainability: Math.min(Math.max(Math.floor(Math.random() * 30) + 60, 60), 100),
          testCoverage: Math.min(Math.max(Math.floor(Math.random() * 60) + 20, 20), 100),
          securityScore: Math.min(Math.max(Math.floor(Math.random() * 20) + 70, 70), 100),
          performance: Math.min(Math.max(Math.floor(Math.random() * 25) + 65, 65), 100)
        }
      };

      setAnalysis(result);
      onAnalysisComplete?.(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : '分析失败');
    } finally {
      setLoading(false);
    }
  }, [code, language, onAnalysisComplete]);

  // 自动分析
  useEffect(() => {
    if (code && language) {
      analyzeCode();
    }
  }, [code, language, analyzeCode]);

  const getGradeColor = (score: number) => {
    if (score >= 90) return '#10b981';
    if (score >= 80) return '#3b82f6';
    if (score >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const getGradeLabel = (score: number) => {
    if (score >= 90) return '优秀';
    if (score >= 80) return '良好';
    if (score >= 70) return '中等';
    return '需改进';
  };

  if (loading) {
    return (
      <ProfessionalCard title="代码分析仪表板" variant="glass">
        <div className="analysis-loading">
          <div className="analysis-loading__animation">
            <div className="analysis-loading__spinner"></div>
            <div className="analysis-loading__dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
          <div className="analysis-loading__text">
            <h4>AI 正在深度分析代码...</h4>
            <p>正在识别代码结构、计算质量指标、检测潜在问题</p>
            <ProgressBar value={75} showPercentage variant="gradient" />
          </div>
        </div>
      </ProfessionalCard>
    );
  }

  if (error) {
    return (
      <ProfessionalCard title="代码分析仪表板" variant="glass">
        <div className="analysis-error">
          <div className="analysis-error__icon">⚠️</div>
          <h4>分析失败</h4>
          <p>{error}</p>
          <ProfessionalButton onClick={analyzeCode} variant="primary" size="sm">
            重新分析
          </ProfessionalButton>
        </div>
      </ProfessionalCard>
    );
  }

  if (!analysis) {
    return (
      <ProfessionalCard title="代码分析仪表板" variant="glass">
        <div className="analysis-empty">
          <div className="analysis-empty__icon">📊</div>
          <h4>等待代码分析</h4>
          <p>请选择一个代码文件以查看详细分析结果</p>
        </div>
      </ProfessionalCard>
    );
  }

  return (
    <div className="code-analysis-dashboard">
      {/* 总览卡片 */}
      <div className="dashboard-overview">
        <div className="dashboard-header">
          <div className="dashboard-title">
            <h2>代码质量分析报告</h2>
            <StatusIndicator status="success" text="分析完成" variant="badge" />
          </div>
          <ProfessionalButton
            onClick={analyzeCode}
            variant="ghost"
            size="sm"
            icon="🔄"
          >
            重新分析
          </ProfessionalButton>
        </div>

        {/* 关键指标 */}
        <div className="metrics-grid">
          <MetricCard
            title="代码行数"
            value={analysis.metrics.linesOfCode.toLocaleString()}
            description={`文件 ${fileName}`}
            icon="📝"
            variant="gradient"
          />
          <MetricCard
            title="函数数量"
            value={analysis.metrics.functions}
            description="独立函数定义"
            icon="⚡"
          />
          <MetricCard
            title="类数量"
            value={analysis.metrics.classes}
            description="面向对象组件"
            icon="🏗️"
          />
          <MetricCard
            title="复杂度"
            value={analysis.metrics.complexity}
            description={getGradeLabel(analysis.metrics.complexity)}
            icon="🧮"
            change={{
              value: analysis.metrics.complexity > 70 ? -15 : 5,
              type: analysis.metrics.complexity > 70 ? 'decrease' : 'increase'
            }}
          />
        </div>
      </div>

      {/* 详细分析标签页 */}
      <Tabs
        tabs={[
          {
            id: 'overview',
            label: '总览',
            icon: '📊',
            content: (
              <div className="tab-content">
                <div className="summary-section">
                  <h4>代码概览</h4>
                  <p>{analysis.summary}</p>
                </div>

                <div className="quality-scores">
                  <h4>质量评分</h4>
                  <div className="scores-grid">
                    <div className="score-item">
                      <div className="score-header">
                        <span>可维护性</span>
                        <span style={{ color: getGradeColor(analysis.metrics.maintainability) }}>
                          {analysis.metrics.maintainability}%
                        </span>
                      </div>
                      <ProgressBar
                        value={analysis.metrics.maintainability}
                        color={analysis.metrics.maintainability >= 80 ? 'green' : analysis.metrics.maintainability >= 70 ? 'yellow' : 'red'}
                        variant="gradient"
                      />
                    </div>
                    <div className="score-item">
                      <div className="score-header">
                        <span>测试覆盖率</span>
                        <span style={{ color: getGradeColor(analysis.metrics.testCoverage) }}>
                          {analysis.metrics.testCoverage}%
                        </span>
                      </div>
                      <ProgressBar
                        value={analysis.metrics.testCoverage}
                        color={analysis.metrics.testCoverage >= 80 ? 'green' : analysis.metrics.testCoverage >= 60 ? 'yellow' : 'red'}
                        variant="gradient"
                      />
                    </div>
                    <div className="score-item">
                      <div className="score-header">
                        <span>安全性评分</span>
                        <span style={{ color: getGradeColor(analysis.metrics.securityScore) }}>
                          {analysis.metrics.securityScore}%
                        </span>
                      </div>
                      <ProgressBar
                        value={analysis.metrics.securityScore}
                        color={analysis.metrics.securityScore >= 85 ? 'green' : 'yellow'}
                        variant="gradient"
                      />
                    </div>
                    <div className="score-item">
                      <div className="score-header">
                        <span>性能评分</span>
                        <span style={{ color: getGradeColor(analysis.metrics.performance) }}>
                          {analysis.metrics.performance}%
                        </span>
                      </div>
                      <ProgressBar
                        value={analysis.metrics.performance}
                        color={analysis.metrics.performance >= 85 ? 'green' : 'yellow'}
                        variant="gradient"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )
          },
          {
            id: 'components',
            label: '组件结构',
            icon: '🧩',
            badge: analysis.components.length,
            content: (
              <div className="tab-content">
                <h4>主要组件</h4>
                <div className="components-list">
                  {analysis.components.map((component, index) => (
                    <div key={index} className="component-item">
                      <div className="component-icon">📦</div>
                      <div className="component-info">
                        <h5>{component}</h5>
                        <p>核心功能模块，负责特定的业务逻辑处理</p>
                      </div>
                      <StatusIndicator status="success" variant="dot" />
                    </div>
                  ))}
                </div>
              </div>
            )
          },
          {
            id: 'patterns',
            label: '设计模式',
            icon: '🎨',
            badge: analysis.patterns.length,
            content: (
              <div className="tab-content">
                <h4>识别的设计模式</h4>
                <div className="patterns-grid">
                  {analysis.patterns.map((pattern, index) => (
                    <ProfessionalCard key={index} variant="elevated" className="pattern-card">
                      <div className="pattern-header">
                        <span className="pattern-icon">🔷</span>
                        <h5>{pattern}</h5>
                      </div>
                      <p>该模式的正确应用提高了代码的可维护性和扩展性</p>
                    </ProfessionalCard>
                  ))}
                </div>
              </div>
            )
          },
          {
            id: 'issues',
            label: '问题与建议',
            icon: '⚠️',
            badge: analysis.issues.length + analysis.recommendations.length,
            content: (
              <div className="tab-content">
                <div className="issues-section">
                  <h4>发现的问题</h4>
                  <div className="issues-list">
                    {analysis.issues.map((issue, index) => (
                      <div key={index} className="issue-item warning">
                        <span className="issue-icon">⚠️</span>
                        <span>{issue}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="recommendations-section">
                  <h4>改进建议</h4>
                  <div className="recommendations-list">
                    {analysis.recommendations.map((rec, index) => (
                      <div key={index} className="recommendation-item">
                        <span className="recommendation-icon">💡</span>
                        <span>{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          }
        ]}
        defaultTab="overview"
        onTabChange={setActiveTab}
        variant="pills"
        size="md"
      />
    </div>
  );
};

export default CodeAnalysisDashboard;